
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Search, Filter, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import PageTransition from "@/components/PageTransition";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { format, isPast, differenceInDays } from "date-fns";

import AddItemModalWrapper from "@/components/inventory/AddItem/AddItemModalWrapper";
import InventoryList from "@/components/inventory/InventoryList";
import EmptyInventoryState from "@/components/inventory/EmptyInventoryState";
import ExportReportModal from "@/components/inventory/ExportReportModal";
import { InventoryItem } from "@/types/inventory";

const Inventory = () => {
  const { user } = useAuth();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  
  const fetchInventoryItems = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('inventory_items')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      setInventoryItems(data || []);
    } catch (error: any) {
      console.error("Error fetching inventory:", error.message);
      toast.error("Failed to load inventory items");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (!user) return;
    
    fetchInventoryItems();
    
    const channel = supabase
      .channel('inventory_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'inventory_items',
          filter: `user_id=eq.${user.id}`
        }, 
        (payload) => {
          console.log('Change received!', payload);
          fetchInventoryItems();
        }
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);
  
  const filteredItems = inventoryItems.filter(item => {
    const matchesSearch = 
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.barcode && item.barcode.includes(searchQuery));
    
    let matchesTab = true;
    if (activeTab === "expiring-soon") {
      matchesTab = item.has_expiry && item.expiry_date && 
                  !isPast(new Date(item.expiry_date)) && 
                  differenceInDays(new Date(item.expiry_date), new Date()) <= 7;
    } else if (activeTab === "expired") {
      matchesTab = item.has_expiry && item.expiry_date && 
                  isPast(new Date(item.expiry_date));
    }
    
    const matchesCategory = !activeFilter || item.category === activeFilter;
    
    return matchesSearch && matchesTab && matchesCategory;
  });
  
  const handleItemAdded = () => {
    toast.success("Item added to inventory", {
      position: "bottom-right",
      duration: 3000,
    });
    fetchInventoryItems();
  };
  
  const categories = [...new Set(inventoryItems.map(item => item.category))];
  
  return (
    <PageTransition className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <h1 className="text-2xl font-bold mb-4 md:mb-0">Inventory Management</h1>
          <div className="flex gap-2">
            <Button 
              onClick={() => setIsExportModalOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <FileText className="mr-2 h-4 w-4" /> Export Report
            </Button>
            <Button 
              onClick={() => setIsAddModalOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="mr-2 h-4 w-4" /> Add Item
            </Button>
          </div>
        </div>
        
        <div className="mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by name, description or barcode..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="flex items-center">
                  <Filter className="mr-2 h-4 w-4" />
                  {activeFilter || "All Categories"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setActiveFilter(null)}>
                  All Categories
                </DropdownMenuItem>
                {categories.map((category) => (
                  <DropdownMenuItem 
                    key={category} 
                    onClick={() => setActiveFilter(category)}
                  >
                    {category}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="all">All Items</TabsTrigger>
            <TabsTrigger value="expiring-soon">Expiring Soon</TabsTrigger>
            <TabsTrigger value="expired">Expired</TabsTrigger>
          </TabsList>
          
          <TabsContent value="all" className="mt-4">
            {inventoryItems.length === 0 && !loading ? (
              <EmptyInventoryState />
            ) : (
              <InventoryList 
                items={filteredItems} 
                loading={loading} 
                onRefresh={fetchInventoryItems} 
              />
            )}
          </TabsContent>
          
          <TabsContent value="expiring-soon" className="mt-4">
            {filteredItems.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-muted-foreground">No items expiring soon. Good job!</p>
                </CardContent>
              </Card>
            ) : (
              <InventoryList 
                items={filteredItems} 
                loading={loading} 
                onRefresh={fetchInventoryItems} 
              />
            )}
          </TabsContent>
          
          <TabsContent value="expired" className="mt-4">
            {filteredItems.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-muted-foreground">No expired items. Great work!</p>
                </CardContent>
              </Card>
            ) : (
              <InventoryList 
                items={filteredItems} 
                loading={loading} 
                onRefresh={fetchInventoryItems} 
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
      
      <AddItemModalWrapper 
        open={isAddModalOpen} 
        onOpenChange={setIsAddModalOpen} 
        onItemAdded={handleItemAdded}
      />
      
      <ExportReportModal
        open={isExportModalOpen}
        onOpenChange={setIsExportModalOpen}
        inventoryItems={inventoryItems}
        user={user}
      />
    </PageTransition>
  );
};

export default Inventory;
